/**
 * The boot mark as an SVG that animates itself: the frames of
 * `boot-mark-frames.ts`, sampled into CSS keyframes inside the SVG.
 *
 * **Why CSS, not Skia or SMIL.** The boot screen is the first thing the page
 * draws, before any script (index.html), and it stays only while the scripts
 * and the database load. A CSS animation runs as soon as the page has its
 * styles, so the mark moves from the first frame. Skia moves nothing until its
 * 7 MB library has loaded, which on most boots is after the boot screen has
 * gone, and SMIL waits for the page's load event, which waits for every script.
 *
 * **One string, two places.** The Vite config writes `bootMarkSvg()` into
 * index.html, and `boot-mark.tsx` draws the same string, so React's first
 * draw is the drawing the page already shows (e2e/shell.spec.ts holds the two
 * equal).
 *
 * **Sampled, with straight lines between.** Every frame of a block is the
 * block at rest, moved and scaled (`blockTransform`), so a block is its path
 * at rest and a transform per sample. The ink is a mask: a ramp from clear to
 * solid, moved and stretched to the frames' ink edge. The holds need no
 * samples; the sink and the rise get `STEPS` each.
 *
 * **Reduced motion** turns the animations off, and what is left is each
 * element's own geometry: the rest frame, the whole mark in full ink.
 *
 * The SVG's own units start at the mark's corner, so a transform's origin,
 * `0 0`, is that corner in every browser.
 *
 * Pure, and imported by the Vite config: relative imports only.
 */
import { blockTransform, bootMarkFrame, inkEdge, MARK_BLOCKS, MARK_BOX, PERIOD_MS, RISE, SINK, type MarkBlock } from './boot-mark-frames';

/** The mark's height on the boot screen. */
export const BOOT_MARK_SIZE = 56;

/** Where the Vite config writes the mark into index.html. */
export const BOOT_MARK_SLOT = '<!--boot-mark-->';

/** Samples across the sink and across the rise. Enough that straight lines between them read as the curve. */
const STEPS = 12;

/** How far the ink's mask reaches past its edge, above and below, in edge widths: past the mark at every frame. */
export const INK_REACH = 8;

/** The phases sampled: the ends of each hold, and `STEPS` steps across the sink and the rise. */
export const keyPhases = (): number[] => {
  const across = (from: number, to: number): number[] =>
    Array.from({ length: STEPS + 1 }, (_, step) => from + ((to - from) * step) / STEPS);
  return [0, ...across(SINK.from, SINK.to), ...across(RISE.from, RISE.to), 1];
};

const fixed = (value: number, places: number): string => String(Number(value.toFixed(places)) || 0);
/** A length in the SVG's units: one is under a tenth of a pixel at the boot screen's size. */
const length = (value: number): string => fixed(value, 1);

/** A block at rest in the SVG's own units, which start at the mark's corner. */
const own = ({ box, corners }: MarkBlock): MarkBlock => ({
  box: [box[0] - MARK_BOX.x, box[1] - MARK_BOX.y, box[2] - MARK_BOX.x, box[3] - MARK_BOX.y],
  corners,
});

/** One block's outline, clockwise from its upper-left corner, with an arc at every rounded corner. */
export const blockPath = (block: MarkBlock): string => {
  const { box, corners } = own(block);
  const [left, top, right, bottom] = box;
  const [upperLeft, upperRight, lowerRight, lowerLeft] = corners;
  const arc = (radius: number, x: number, y: number): string =>
    radius > 0 ? `A${length(radius)} ${length(radius)} 0 0 1 ${length(x)} ${length(y)}` : `L${length(x)} ${length(y)}`;
  return [
    `M${length(left)} ${length(top + upperLeft)}`,
    arc(upperLeft, left + upperLeft, top),
    `H${length(right - upperRight)}`,
    arc(upperRight, right, top + upperRight),
    `V${length(bottom - lowerRight)}`,
    arc(lowerRight, right - lowerRight, bottom),
    `H${length(left + lowerLeft)}`,
    arc(lowerLeft, left, bottom - lowerLeft),
    'Z',
  ].join('');
};

/** The ink's edge, in the SVG's units, as the mask's move and stretch. */
const inkTransform = (frame: ReturnType<typeof bootMarkFrame>): { readonly y: number; readonly stretch: number } => {
  const { clear, solid } = inkEdge(frame);
  return { y: clear - MARK_BOX.y, stretch: solid - clear };
};

/** Each block's and the ink's transform at every key phase, as CSS. */
export const keyTransforms = (): { readonly phases: number[]; readonly blocks: string[][]; readonly ink: string[] } => {
  const phases = keyPhases();
  const frames = phases.map(bootMarkFrame);
  const blocks = MARK_BLOCKS.map((rest, index) =>
    frames.map(({ blocks: shown }) => {
      const { scale, x, y } = blockTransform(rest, shown[index] ?? rest);
      // The same move in the SVG's units, whose origin is the mark's corner.
      const ownX = x + (scale - 1) * MARK_BOX.x;
      const ownY = y + (scale - 1) * MARK_BOX.y;
      return `translate(${length(ownX)}px,${length(ownY)}px) scale(${fixed(scale, 4)})`;
    }),
  );
  const ink = frames.map((frame) => {
    const { y, stretch } = inkTransform(frame);
    return `translate(0px,${length(y)}px) scale(1,${length(stretch)})`;
  });
  return { phases, blocks, ink };
};

const CLASSES = ['boot-mark-0', 'boot-mark-1', 'boot-mark-2'] as const;
const INK_CLASS = 'boot-mark-ink';

const style = (): string => {
  const { phases, blocks, ink } = keyTransforms();
  const keyframes = (name: string, transforms: readonly string[]): string =>
    `@keyframes ${name}{${phases.map((phase, index) => `${fixed(phase * 100, 3)}%{transform:${transforms[index] ?? 'none'}}`).join('')}}`;
  const named = [...CLASSES.map((name, index) => ({ name, transforms: blocks[index] ?? [] })), { name: INK_CLASS, transforms: ink }];
  const selectors = named.map(({ name }) => `.${name}`).join(',');
  return (
    `${selectors}{transform-box:view-box;transform-origin:0 0}` +
    named.map(({ name }) => `.${name}{animation:${name} ${PERIOD_MS}ms linear infinite}`).join('') +
    `@media (prefers-reduced-motion:reduce){${selectors}{animation:none}}` +
    named.map(({ name, transforms }) => keyframes(name, transforms)).join('')
  );
};

/** The boot mark, `size` tall, its mask named after `id`. Still, or at its clock's 0, it is the whole mark. */
export const bootMarkSvg = ({ size = BOOT_MARK_SIZE, id }: { size?: number; id: string }): string => {
  const rest = inkTransform(bootMarkFrame(0));
  const mask =
    `<linearGradient id="${id}-ramp" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="1">` +
    '<stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#fff"/></linearGradient>' +
    `<mask id="${id}" maskUnits="userSpaceOnUse" x="0" y="0" width="${MARK_BOX.width}" height="${MARK_BOX.height}">` +
    `<rect class="${INK_CLASS}" x="0" y="${-INK_REACH}" width="${MARK_BOX.width}" height="${2 * INK_REACH + 1}" fill="url(#${id}-ramp)" ` +
    `transform="translate(0 ${length(rest.y)}) scale(1 ${length(rest.stretch)})"/></mask>`;
  const paths = MARK_BLOCKS.map((block, index) => `<path class="${CLASSES[index] ?? ''}" d="${blockPath(block)}"/>`).join('');
  const width = Math.round((size * MARK_BOX.width) / MARK_BOX.height);
  return (
    `<svg aria-hidden="true" width="${width}" height="${size}" viewBox="0 0 ${MARK_BOX.width} ${MARK_BOX.height}" class="shrink-0 text-foreground">` +
    `<style>${style()}</style><defs>${mask}</defs><g mask="url(#${id})" fill="currentColor">${paths}</g></svg>`
  );
};
